import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { LotCartouche, LotCartouchePayload, LotsCartouchesService } from '../../../services/lots-cartouches.service';

@Component({
  selector: 'app-lots-cartouches',
  imports: [CommonModule, FormsModule],
  templateUrl: './lots-cartouches.component.html',
  styleUrl: './lots-cartouches.component.scss'
})
export class LotsCartouchesComponent implements OnInit {
  protected pageMode: 'gerer' | 'voir' | 'creer' | 'modifier' | 'supprimer' = 'gerer';
  protected lots: LotCartouche[] = [];
  protected selectedId = '';
  protected nouveauNom = '';
  protected nomEdition = '';
  protected nouvelleDate = '';
  protected dateEdition = '';
  protected nouveauNombreCartouches: number | null = null;
  protected nombreCartouchesEdition: number | null = null;
  protected loading = false;
  protected loadingList = false;
  protected deletingLotId: number | null = null;
  protected success = '';
  protected error = '';

  constructor(
    private readonly lotsService: LotsCartouchesService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadLots();
      }
    });
    this.loadLots();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'voir' || this.pageMode === 'supprimer';
  }

  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'supprimer';
  }

  private syncPageMode(): void {
    const lastPart = this.router.url.split('/').pop() ?? '';
    if (['voir', 'creer', 'modifier', 'supprimer'].includes(lastPart)) {
      this.pageMode = lastPart as 'voir' | 'creer' | 'modifier' | 'supprimer';
    } else {
      this.pageMode = 'gerer';
    }
  }

  protected onSelectChange(): void {
    const selected = this.lots.find((lot) => lot.id === Number(this.selectedId));
    if (selected) {
      this.nomEdition = selected.nom;
      this.dateEdition = selected.date_reception;
      this.nombreCartouchesEdition = selected.nombre_cartouches;
      this.cdr.markForCheck();
    }
  }

  protected createLot(): void {
    if (!this.nouveauNom.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: LotCartouchePayload = {
      nom: this.nouveauNom.trim(),
      date_reception: this.nouvelleDate || undefined,
      nombre_cartouches: this.nouveauNombreCartouches ?? undefined
    };

    this.zone.run(() => {
      this.lotsService.create(payload).subscribe({
        next: () => {
          this.success = 'Lot créé avec succès.';
          this.nouveauNom = '';
          this.nouvelleDate = '';
          this.nouveauNombreCartouches = null;
          this.loadLots();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected renameLot(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un lot.';
      return;
    }
    if (!this.nomEdition.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: LotCartouchePayload = {
      nom: this.nomEdition.trim(),
      date_reception: this.dateEdition || undefined,
      nombre_cartouches: this.nombreCartouchesEdition ?? undefined
    };

    this.zone.run(() => {
      this.lotsService.update(Number(this.selectedId), payload).subscribe({
        next: () => {
          this.success = 'Lot modifié avec succès.';
          this.loadLots();
          this.selectedId = '';
          this.nomEdition = '';
          this.dateEdition = '';
          this.nombreCartouchesEdition = null;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la modification: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteLot(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un lot.';
      return;
    }

    const lotId = Number(this.selectedId);
    const selectedLot = this.lots.find((lot) => lot.id === lotId);
    const lotName = selectedLot?.nom ?? `#${lotId}`;

    this.verifyLotNotLinkedBeforeDelete(lotId, lotName, () => {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce lot?')) {
        return;
      }

      this.loading = true;
      this.error = '';
      this.success = '';

      this.zone.run(() => {
        this.lotsService.delete(lotId).subscribe({
          next: () => {
            this.success = 'Lot supprimé avec succès.';
            this.loadLots();
            this.selectedId = '';
            this.nomEdition = '';
            this.nombreCartouchesEdition = null;
            this.dateEdition = '';
            this.loading = false;
            this.cdr.markForCheck();
          },
          error: (err: HttpErrorResponse) => {
            this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
            this.loading = false;
            this.cdr.markForCheck();
          }
        });
      });
    });
  }

  protected deleteLotRow(lot: LotCartouche): void {
    this.verifyLotNotLinkedBeforeDelete(lot.id, lot.nom, () => {
      if (!confirm(`Êtes-vous sûr de vouloir supprimer le lot "${lot.nom}"?`)) {
        return;
      }

      this.deletingLotId = lot.id;

      this.zone.run(() => {
        this.lotsService.delete(lot.id).subscribe({
          next: () => {
            this.success = 'Lot supprimé avec succès.';
            this.loadLots();
            this.deletingLotId = null;
            this.cdr.markForCheck();
          },
          error: (err: HttpErrorResponse) => {
            this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
            this.deletingLotId = null;
            this.cdr.markForCheck();
          }
        });
      });
    });
  }

  private verifyLotNotLinkedBeforeDelete(lotId: number, lotName: string, onAllowedDelete: () => void): void {
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.lotsService.getLinkedToCapteur(lotId).subscribe({
        next: (linkStatus) => {
          if (linkStatus.linked) {
            const message = `Suppression impossible (RESTRICT): le lot "${lotName}" est lié à ${linkStatus.linked_capteurs_count} capteur(s).`;
            this.error = message;
            alert(message);
            this.cdr.markForCheck();
            return;
          }

          onAllowedDelete();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la vérification du lot: ${this.getErrorMessage(err)}`;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }

  private loadLots(): void {
    this.loadingList = true;
    this.error = '';

    this.zone.run(() => {
      this.lotsService.list().subscribe({
        next: (lots) => {
          this.lots = lots;
          this.loadingList = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loadingList = false;
          this.cdr.markForCheck();
        }
      });
    });
  }
}
